'use client';

import { useEffect, useRef } from 'react';
import { debounce } from '@bts-web/utils-functions';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { getAppConfig } from '@bts-web/core-app-config';
import { getUrlToRedirectAfterLogoutAction } from '../actions';
import { useClientUserInfo } from '../../common/userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';

// 5 minutes
export const INACTIVITY_TIMEOUT_IN_MS = 300000;

const userEvents = [
  'mousedown',
  'mousemove',
  'wheel',
  'keydown',
  'touchstart',
  'scroll',
];

const customEvents = [
  // custom event for identifying a trade offer re-creation
  'onTradeOfferRefetched',
];

const events = [...userEvents, ...customEvents];

let timeout: ReturnType<typeof setTimeout>;

let interval: ReturnType<typeof setInterval>;

type UserActivityMessageType =
  | 'USER_ACTIVE'
  | 'USER_CLEAR_ACTIVITY'
  | 'USER_LOGOUT';

type UserActivityMessage = {
  type: UserActivityMessageType;
  tabId: string;
  redirectUrl?: string;
};

// Toggle for development debugging - manually change this to enable/disable detailed user activity logging
const ENABLE_USER_ACTIVITY_DEBUG = true;

const CheckUserInactivity = () => {
  const tabId = useRef(`tab-${Date.now()}-${Math.floor(Math.random() * 1000)}`);

  const { appName } = getAppConfig();

  const userInfo = useClientUserInfo();

  const userInfoRef = useRef(userInfo);

  userInfoRef.current = userInfo;

  // use ref to avoid duplicate channels in case of re-renders
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  const setInactivityTimeout = () => {
    if (ENABLE_USER_ACTIVITY_DEBUG) {
      console.info('[USER_ACTIVITY] SETTING USER INACTIVITY TIMEOUT');
    }

    clearTimeout(timeout);

    clearInterval(interval);

    const startedTime = new Date();

    timeout = setTimeout(async () => {
      if (ENABLE_USER_ACTIVITY_DEBUG) {
        console.info('[USER_ACTIVITY] User inactive for too long, logging out');
      }

      // immediately notify other tabs to clear their timeouts
      if (broadcastChannelRef.current) {
        if (ENABLE_USER_ACTIVITY_DEBUG) {
          console.info(
            '[USER_ACTIVITY] Broadcasting USER_CLEAR_ACTIVITY to other tabs',
          );
        }

        broadcastChannelRef.current.postMessage({
          type: 'USER_CLEAR_ACTIVITY',
          tabId: tabId.current,
        });
      }

      try {
        let redirectUrl = await getUrlToRedirectAfterLogoutAction();

        if (redirectUrl?.startsWith(NEXT_ROUTES.AUTH_LOGOUT)) {
          redirectUrl = `${redirectUrl}&auto=1`;

          if (userInfoRef.current?.isIslamicUser) {
            redirectUrl = `${redirectUrl}&isIslamicUser=1`;
          }
        }

        if (ENABLE_USER_ACTIVITY_DEBUG) {
          console.info(
            '[USER_ACTIVITY] Logout action successful, redirecting...',
          );
        }

        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({
            type: 'USER_LOGOUT',
            tabId: tabId.current,
            redirectUrl,
          });
        }

        cleanupActivityHandlers();

        window.location.replace(redirectUrl as string);
      } catch (error) {
        console.error('[USER_ACTIVITY] Logout action failed:', error);

        if (ENABLE_USER_ACTIVITY_DEBUG) {
          console.info('[USER_ACTIVITY] Restarting inactivity timer...');
        }

        setInactivityTimeout();
      }
    }, INACTIVITY_TIMEOUT_IN_MS);

    if (ENABLE_USER_ACTIVITY_DEBUG) {
      interval = setInterval(() => {
        const currentTime = new Date();

        const timeDiff = currentTime.getTime() - startedTime.getTime();

        const timeRemaining = INACTIVITY_TIMEOUT_IN_MS - timeDiff;

        const remainingMinutes = Math.floor(timeRemaining / 60000);

        const remainingSeconds = Math.floor((timeRemaining % 60000) / 1000);

        console.info(
          `[USER_ACTIVITY] ${remainingMinutes}m ${remainingSeconds}s remaining until logout`,
        );
      }, 30000); // Check every 30 seconds
    }
  };

  const broadcastUserActivity = () => {
    const message: UserActivityMessage = {
      type: 'USER_ACTIVE',
      tabId: tabId.current,
    };

    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage(message);
    }

    setInactivityTimeout();
  };

  const initializeBroadcastChannel = () => {
    broadcastChannelRef.current = new BroadcastChannel(
      `${appName}-current-user-activity`,
    );

    broadcastChannelRef.current.onmessage = (event) => {
      const message = event.data as UserActivityMessage;

      if (message.tabId === tabId.current) {
        // Ignore messages from the same tab
        return;
      }

      if (ENABLE_USER_ACTIVITY_DEBUG) {
        console.info(
          `[USER_ACTIVITY] Received ${message.type} from tab ${message.tabId}`,
        );
      }

      if (message.type === 'USER_ACTIVE') {
        setInactivityTimeout();
      } else if (message.type === 'USER_CLEAR_ACTIVITY') {
        clearTimeout(timeout);

        clearInterval(interval);
      } else if (message.type === 'USER_LOGOUT') {
        cleanupActivityHandlers();

        window.location.replace(message?.redirectUrl as string);
      }
    };
  };

  const cleanupActivityHandlers = () => {
    clearTimeout(timeout);

    clearInterval(interval);

    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.close();
    }

    broadcastChannelRef.current = null;
  };

  const debouncedBroadcastUserActivity = debounce(broadcastUserActivity, 500);

  useEffect(() => {
    initializeBroadcastChannel();

    broadcastUserActivity();

    // Add event listeners
    events.forEach((userEvent) => {
      document.addEventListener(userEvent, debouncedBroadcastUserActivity);
    });

    return () => {
      cleanupActivityHandlers();

      events.forEach((userEvent) => {
        document.removeEventListener(userEvent, debouncedBroadcastUserActivity);
      });
    };
  }, []);

  return null;
};

export { CheckUserInactivity };
